import { Flex, type FlexProps } from '@mantine/core';
import { useIntersection } from '@mantine/hooks';
import { useAutoLoadMore, useChatScroll } from '../../react';
import {
  createContext,
  useContext,
  useState,
  type ReactNode,
  type RefCallback,
} from 'react';
import classes from './chat-scroll-area.module.css';

export type ChatLoadMore = {
  hasMore: boolean;
  isLoading: boolean;
  hasError: boolean;
  onLoadMore: () => unknown;
};

export type ChatScrollAreaProps = Omit<FlexProps, 'direction' | 'ref'> & {
  children: ReactNode;
  loadMore: ChatLoadMore;
  /** Changes when the user's own message becomes the newest one. */
  scrollToLatestKey?: string | number;
};

const ChatLoadMoreTriggerContext = createContext<RefCallback<HTMLDivElement>>(
  () => undefined
);

/**
 * Newest-first message list: the latest message sits at the bottom, older
 * history loads when `ChatLoadMoreTrigger` scrolls into view.
 */
export const ChatScrollArea = ({
  children,
  loadMore,
  scrollToLatestKey,
  className,
  ...flexProps
}: ChatScrollAreaProps) => {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const { ref: triggerRef, entry } = useIntersection<HTMLDivElement>({
    root: container,
    threshold: 0.1,
  });

  useAutoLoadMore({
    isTriggerVisible: entry?.isIntersecting ?? false,
    hasMore: loadMore.hasMore,
    isLoading: loadMore.isLoading,
    hasError: loadMore.hasError,
    loadMore: loadMore.onLoadMore,
  });
  useChatScroll({ container, scrollToLatestKey });

  return (
    <Flex
      {...flexProps}
      ref={setContainer}
      direction='column-reverse'
      className={[classes.scrollArea, className].filter(Boolean).join(' ')}
    >
      <ChatLoadMoreTriggerContext.Provider value={triggerRef}>
        {children}
      </ChatLoadMoreTriggerContext.Provider>
    </Flex>
  );
};

/** Place after the oldest message: the list loads more when it is visible. */
export const ChatLoadMoreTrigger = () => {
  const triggerRef = useContext(ChatLoadMoreTriggerContext);

  return (
    <div
      ref={triggerRef}
      style={{ height: 30, width: '100%', visibility: 'hidden' }}
    />
  );
};
